// Building blocks for the template registry. Pure (no canvas, no DOM) —
// every template builds in Node and during SSR exactly as in the browser.
//
// Text is positioned by its BASELINE (the first line's alphabetic baseline,
// mm from the label top) because that is how label type is laid out; the
// element box is then derived from the face's fallback metrics with the same
// line-box maths render.ts uses (half-leading + ascent), so the baseline
// lands where the template says in the editor, the preview and the download.
// Element ids come from newId(), so they are fresh every time build() runs.

import { getFont, type FontKey } from "../fonts";
import { getOrnament, type OrnamentKey } from "../ornaments";
import { PALETTE_HEX } from "../palette";
import {
  CANVAS_VERSION,
  LABEL_MM,
  newId,
  type BottleType,
  type CanvasData,
  type ImageElement,
  type LabelElement,
  type LineElement,
  type OrnamentElement,
  type RectElement,
  type TextAlign,
  type TextElement,
} from "../schema";

/** The seven house colours (templates use nothing else). */
export const INK = PALETTE_HEX;

const round2 = (v: number) => Math.round(v * 100) / 100;

export function labelWidth(bottle: BottleType): number {
  return LABEL_MM[bottle].width;
}

/** A complete current-version document. */
export function doc(bottle: BottleType, background: string, elements: LabelElement[]): CanvasData {
  const mm = LABEL_MM[bottle];
  return {
    version: CANVAS_VERSION,
    bottle,
    label: { width_mm: mm.width, height_mm: mm.height },
    background: { color: background },
    elements,
  };
}

export interface TextSpec {
  text: string;
  font: FontKey;
  /** Cap height, mm. */
  size: number;
  color: string;
  /** First-line alphabetic baseline, mm from the label top. */
  baseline: number;
  /** Box left and width, mm. */
  x: number;
  width: number;
  align?: TextAlign;
  bold?: boolean;
  italic?: boolean;
  /** Letter-spacing, em. */
  tracking?: number;
  lineHeight?: number;
}

/** First-line baseline offset from the box top (mm) for a face at a cap height. */
export function baselineOffset(font: FontKey, size: number, lineHeight: number): number {
  const m = getFont(font).metrics;
  const fontMm = size / m.capHeight;
  const advance = fontMm * lineHeight;
  return (advance - (m.ascent + m.descent) * fontMm) / 2 + m.ascent * fontMm;
}

export function text(spec: TextSpec): TextElement {
  const lineHeight = spec.lineHeight ?? 1.2;
  const lines = Math.max(1, spec.text.split("\n").length);
  const m = getFont(spec.font).metrics;
  const advance = (spec.size / m.capHeight) * lineHeight;
  return {
    id: newId(),
    type: "text",
    x: round2(spec.x),
    y: round2(spec.baseline - baselineOffset(spec.font, spec.size, lineHeight)),
    width: round2(spec.width),
    height: round2(advance * lines),
    text: spec.text,
    font: spec.font,
    size_mm: spec.size,
    color: spec.color,
    align: spec.align ?? "center",
    weight: spec.bold ? "bold" : "regular",
    italic: !!spec.italic,
    letter_spacing_em: spec.tracking ?? 0,
    line_height: lineHeight,
  };
}

/** Centred text across the label with `margin` mm each side. */
export function centred(
  bottle: BottleType,
  spec: Omit<TextSpec, "x" | "width" | "align"> & { margin?: number },
): TextElement {
  const W = labelWidth(bottle);
  const margin = spec.margin ?? 6;
  return text({ ...spec, x: margin, width: W - 2 * margin, align: "center" });
}

/**
 * An ornament `width` mm wide (height from its aspect), placed so the point
 * (ux, uy) in its SVG units lands on (mx, my) in label mm. Default anchor:
 * the centre of the art.
 */
export function ornament(
  key: OrnamentKey,
  color: string,
  opts: { width: number; at: [number, number]; anchor?: [number, number] },
): OrnamentElement {
  const o = getOrnament(key);
  if (!o) throw new Error(`Unknown ornament ${key}`);
  const vb = o.viewBox;
  const s = opts.width / vb.width;
  const [ux, uy] = opts.anchor ?? [vb.x + vb.width / 2, vb.y + vb.height / 2];
  const width = opts.width;
  const height = vb.height * s;
  return {
    id: newId(),
    type: "ornament",
    asset: key,
    color,
    x: round2(opts.at[0] - (ux - vb.x) * s),
    y: round2(opts.at[1] - (uy - vb.y) * s),
    width: round2(width),
    height: round2(height),
  };
}

/** Where SVG point (ux, uy) of a placed ornament sits on the label, mm. */
export function ornamentPoint(el: OrnamentElement, ux: number, uy: number): [number, number] {
  const vb = getOrnament(el.asset)!.viewBox;
  const s = el.width / vb.width;
  return [round2(el.x + (ux - vb.x) * s), round2(el.y + (uy - vb.y) * s)];
}

/** A horizontal hairline centred on `cx`, its centre line at `cy`. */
export function hairline(color: string, cx: number, cy: number, width: number, thickness = 0.3): LineElement {
  return {
    id: newId(),
    type: "line",
    x: round2(cx - width / 2),
    y: round2(cy - thickness / 2),
    width: round2(width),
    height: thickness,
    stroke: color,
    thickness_mm: thickness,
  };
}

export function rect(
  color: string,
  box: { x: number; y: number; width: number; height: number },
  stroke_mm: number,
  fill: string | null = null,
): RectElement {
  return {
    id: newId(),
    type: "rect",
    x: round2(box.x),
    y: round2(box.y),
    width: round2(box.width),
    height: round2(box.height),
    stroke: color,
    stroke_mm,
    fill,
  };
}

/** A template's logo/photo position: the single image element, empty, with its caption. */
export function imageSlot(box: { x: number; y: number; width: number; height: number }, caption: string): ImageElement {
  return {
    id: newId(),
    type: "image",
    src: "uploaded",
    fit: "contain",
    x: round2(box.x),
    y: round2(box.y),
    width: round2(box.width),
    height: round2(box.height),
    placeholder: caption,
  };
}
